/**
 * Фоновая подкачка чанков кабинета.
 *
 * Каждая страница кабинета — отдельный `() => import(...)`, и без этой
 * подкачки её файлы начинали качаться ровно в ту секунду, когда человек
 * нажимал вкладку: переход упирался в сеть, а не в отрисовку. Здесь мы в
 * простое браузера тянем те же чанки заранее, по одному, чтобы к моменту
 * нажатия они лежали в кэше.
 *
 * Порядок берём из `primaryNavigation` — это и есть порядок вкладок, то есть
 * очередь от самого вероятного следующего шага к наименее вероятному.
 *
 * Подкачка молчаливая: любая ошибка здесь ничего не значит для человека
 * (страница всё равно загрузится при переходе), поэтому она гасится и не
 * попадает ни в консоль, ни в Sentry.
 */
import type { Router } from 'vue-router'

import { primaryNavigation } from '@/shared/config/navigation'

type ChunkLoader = () => Promise<unknown>

interface SaveDataConnection {
  saveData?: boolean
  effectiveType?: string
}

/**
 * Экономия трафика и медленная сеть: качать впрок то, что человек, возможно,
 * не откроет, — значит тратить чужие деньги и чужой канал. В этом режиме
 * подкачки нет, переходы работают как раньше.
 */
function isThriftyNetwork(): boolean {
  const connection = (navigator as Navigator & { connection?: SaveDataConnection }).connection
  if (!connection) return false

  return connection.saveData === true || /2g/.test(connection.effectiveType ?? '')
}

function whenIdle(task: () => void): void {
  const idle = (window as Window & { requestIdleCallback?: typeof requestIdleCallback })
    .requestIdleCallback

  if (typeof idle === 'function') idle(() => task(), { timeout: 3000 })
  else window.setTimeout(task, 300)
}

/** Загрузчики страниц кабинета в порядке вкладок, без уже открытой. */
function collectLoaders(router: Router, skipRouteName?: string): ChunkLoader[] {
  const records = router.getRoutes()
  const loaders: ChunkLoader[] = []

  for (const item of primaryNavigation) {
    if (item.name === skipRouteName) continue

    const component = records.find((record) => record.name === item.name)?.components?.default
    if (typeof component === 'function') loaders.push(component as ChunkLoader)
  }

  return loaders
}

/**
 * Запускает подкачку. Вызывается один раз за сессию: повторный вызов вернёт
 * `false` и ничего не сделает.
 */
let started = false

export function prefetchNavigationChunks(router: Router): boolean {
  if (started) return false
  if (isThriftyNetwork()) return false

  started = true

  const currentName = router.currentRoute.value.name
  const queue = collectLoaders(router, typeof currentName === 'string' ? currentName : undefined)

  // По одному: очередь из десятка параллельных запросов отнимает канал у
  // данных страницы, на которой человек находится прямо сейчас.
  const loadNext = (): void => {
    const load = queue.shift()
    if (!load) return

    Promise.resolve()
      .then(load)
      .catch(() => undefined)
      .then(() => whenIdle(loadNext))
  }

  whenIdle(loadNext)

  return true
}

/** Только для тестов: сбрасывает «уже запускали». */
export function resetPrefetchStateForTests(): void {
  started = false
}
